import { QueryHookOptions } from "@apollo/client";
import { uniqBy } from "lodash";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { hasRole } from "@/auth/helpers";
import { useUser } from "@/auth/useUser";
import { useClientAccessToken } from "@/contexts/clientAccessTokenContext";
import { useServicesAndProductsQuery } from "@/graphql/queries/servicesAndProducts.graphql.types";
import {
  WalletQuery,
  WalletQueryVariables,
  useWalletQuery,
} from "@/graphql/queries/wallet.graphql.types";
import { useIsOTPClientPage } from "@/hooks/client/useIsOTPClientPage";
import { MembershipStatus } from "@/types";
import { MEDICAL_DIRECTOR } from "@/types";
import { useWalletCreditTypes } from "../serviceFlow/wallet/useWalletCreditTypes";

export type WalletItem = {
  id: string;
  name: string;
  amount: number;
  unit?: string;
};

export const shortenCreditLabel = (walletCreditlabel: string) =>
  walletCreditlabel.split(" ")[0];

export const DELETED_SERVICE_NAME = "Deleted service";
export const DELETED_PRODUCT_NAME = "Deleted product";

export const PARTIALLY_PAID_INVOICE_DOLLAR_CREDIT_WARNING =
  "For reporting and billing purposes, you cannot use Dollar Credits to complete a partially paid invoice. For more information, reach out to your PSM";

export type Wallet = WalletQuery["clientByPk"]["wallet"];
type WalletQueryCustomOptions = QueryHookOptions<
  WalletQuery,
  WalletQueryVariables
>;

// Wallet will be also used on the invoice/payment page, then userId has to be passed as an argument
export default function useWallet(
  clientId?: string,
  queryOptions?: WalletQueryCustomOptions
) {
  const {
    query: { slug },
    pathname,
  } = useRouter();
  const isOTPClientPage = useIsOTPClientPage();
  const { clientAccessToken: token, getTokenContext } = useClientAccessToken();
  const { user } = useUser();

  const isMdOrGfe = hasRole(user, [MEDICAL_DIRECTOR]);

  const creditTypes = useWalletCreditTypes();

  const { data: servicesAndProductsData } = useServicesAndProductsQuery({
    // Remove this if you need to display wallet items on client access page
    skip: isOTPClientPage || Boolean(token) || isMdOrGfe,
  });

  // Slug can be used only on client page
  const clientIdToUse =
    clientId || (pathname.includes("clients") ? (slug as string) : null);

  const {
    data: walletData,
    refetch,
    updateQuery: updateWalletQuery,
    loading: walletLoading,
  } = useWalletQuery({
    variables: {
      clientId: clientIdToUse,
    },
    skip: !clientIdToUse || isMdOrGfe,
    ...getTokenContext(),
    ...queryOptions,
  });

  const mappedWalletItems: {
    services: WalletItem[];
    products: WalletItem[];
    serviceProducts: WalletItem[];
  } = useMemo(() => {
    if (!(servicesAndProductsData && walletData?.clientByPk)) {
      return { services: [], products: [], serviceProducts: [] };
    }

    const currentSums = walletData.clientByPk.wallet.currentSums;

    const services = currentSums.services
      .filter((service) => !!+service.sum)
      .map((service) => ({
        id: service.serviceId,
        amount: +service.sum,
        name:
          servicesAndProductsData.medspaServiceMenuItem.find(
            (s) => s.id === service.serviceId
          )?.name || DELETED_SERVICE_NAME,
      }));

    const products = currentSums.products
      .filter((product) => !!+product.sum)
      .map((product) => {
        const { name, unit, productType } =
          servicesAndProductsData.product.find(
            (p) => p.id === product.productId
          ) || { name: DELETED_PRODUCT_NAME, unit: "Units" };

        if (productType === "service") {
          return undefined;
        }

        return {
          id: product.productId,
          amount: +product.sum,
          name,
          unit,
        };
      })
      .filter(Boolean);

    const serviceProducts = currentSums.serviceProducts
      .filter((serviceProduct) => !!+serviceProduct.sum)
      .map((serviceProduct) => {
        const { name, unitOfMeasurement } =
          servicesAndProductsData.medspaServiceProduct.find(
            (p) => p.id === serviceProduct.serviceProductId
          )?.serviceProduct || { name: DELETED_PRODUCT_NAME, unit: "Units" };
        return {
          id: serviceProduct.serviceProductId,
          amount: +serviceProduct.sum,
          name,
          unit: unitOfMeasurement,
        };
      });

    return { services, products, serviceProducts };
  }, [servicesAndProductsData, walletData]);

  const currentCredits = useMemo(
    () => walletData?.clientByPk?.wallet?.currentSums.credits ?? [],
    [walletData]
  );

  // filters out any $0 credits
  const payableCredits = useMemo(
    () => currentCredits.filter((credit) => +credit.sum > 0),
    [currentCredits]
  );

  const cardList = useMemo(() => {
    if (walletLoading || !walletData) return [];
    return uniqBy(walletData?.clientByPk?.stripeData?.stripeCardsList, "id");
  }, [walletData, walletLoading]);

  const defaultCard = useMemo(
    () => cardList.find((card) => card.isDefault) ?? null,
    [cardList]
  );

  const hasActiveMemberships = useMemo(
    () =>
      walletData?.clientByPk?.clientMemberships?.some(
        ({ status }) => status !== MembershipStatus.CANCELED
      ),
    [walletData?.clientByPk?.clientMemberships]
  );

  const hasWalletItemsOrCredits = useMemo(() => {
    if (walletLoading || !walletData) return false;
    const currentSums = walletData?.clientByPk?.wallet?.currentSums;

    if (!currentSums) return false;

    return Boolean(
      currentSums.credits.length ||
        currentSums.services.length ||
        currentSums.products.length ||
        currentSums.serviceProducts.length
    );
  }, [walletData, walletLoading]);

  const getLabelForCreditType = (creditTypeId: string) =>
    creditTypes.find((credit) => credit.id === creditTypeId)?.creditName ?? "";

  const DOLLAR_CREDIT_TYPE_ID = "1";

  const dollarWalletType = useMemo(
    () => creditTypes.find((credit) => credit.id === DOLLAR_CREDIT_TYPE_ID),
    [creditTypes]
  );

  const hasOnlyDollarCredits = useMemo(
    () =>
      payableCredits.length === 1 &&
      payableCredits[0].creditType === DOLLAR_CREDIT_TYPE_ID,
    [payableCredits]
  );

  return {
    loading: walletLoading,
    refetchWallet: refetch,
    wallet: walletData?.clientByPk?.wallet,
    clientId: clientIdToUse,
    cardList,
    defaultCard,
    currentCredits,
    canPayWithWallet: payableCredits.length > 0,
    payableCredits,
    updateWalletQuery,
    services: mappedWalletItems.services,
    products: mappedWalletItems.products,
    serviceProducts: mappedWalletItems.serviceProducts,
    hasWalletItemsOrCredits,
    hasActiveMemberships,
    getLabelForCreditType,
    dollarWalletType,
    hasOnlyDollarCredits,
  };
}
